import { useEffect, useState } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Phone,
  Share,
  MoreVertical,
  PlusSquare,
  Smartphone,
  Bookmark,
  ShieldCheck,
  PhoneCall,
  Pencil,
  Trash2,
} from "lucide-react";

/* GP number is saved on the carer's device only. No server, no account, no
   cross-device sync — matches the no-login standalone nature of this page. */
const GP_STORAGE_KEY = "jcc.safetyCheck.gpContact.v1";

const sanitiseTelDigits = (raw) => {
  // Keep digits and a single leading + if present, strip everything else.
  if (!raw) return "";
  const trimmed = String(raw).trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/[^\d]/g, "");
  return hasPlus ? `+${digits}` : digits;
};

/**
 * Stop & Check — standalone, mobile-first decision-support page.
 *
 * Designed to be saved to a phone home screen by Pro carers and opened
 * mid-shift when something changes or feels off. Intentionally plain,
 * calm and tap-friendly. Not clinical advice.
 */
export default function SafetyCheck() {
  // A subtle on-mount scroll-to-top so a re-tap from the home screen always
  // starts at the top, regardless of where the page was left.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // GP surgery contact — stored locally on this device only.
  const [gp, setGp] = useState({ name: "", phone: "" });
  const [editingGp, setEditingGp] = useState(false);
  const [gpDraft, setGpDraft] = useState({ name: "", phone: "" });

  useEffect(() => {
    try {
      const raw = window.localStorage?.getItem(GP_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setGp({ name: parsed.name || "", phone: parsed.phone || "" });
        }
      }
    } catch {
      /* localStorage unavailable — fall through with empty state */
    }
  }, []);

  const gpTel = sanitiseTelDigits(gp.phone);
  const hasGp = gpTel.length > 0;

  const openEditGp = () => {
    setGpDraft({ name: gp.name, phone: gp.phone });
    setEditingGp(true);
  };
  const cancelEditGp = () => {
    setEditingGp(false);
    setGpDraft({ name: "", phone: "" });
  };
  const saveGp = (e) => {
    e?.preventDefault?.();
    const cleanedPhone = sanitiseTelDigits(gpDraft.phone);
    if (!cleanedPhone) return; // require a real number
    const next = { name: gpDraft.name.trim(), phone: gpDraft.phone.trim() };
    setGp(next);
    try {
      window.localStorage?.setItem(GP_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setEditingGp(false);
  };
  const clearGp = () => {
    setGp({ name: "", phone: "" });
    try {
      window.localStorage?.removeItem(GP_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setEditingGp(false);
  };

  return (
    <div className="bg-[#fafaf7] min-h-[80vh]" data-testid="safety-check-page">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container-jcc py-8 md:py-12">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-teal" aria-hidden="true" />
            <span className="overline text-teal">JCC Pro Carer · Quick Tool</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            Stop &amp; Check
          </h1>
          <p className="mt-4 text-base md:text-lg text-ink-muted leading-relaxed max-w-2xl">
            A calm prompt to help you pause and think clearly when something on
            shift changes, surprises you or doesn&apos;t feel right.
          </p>

          {/* Before you continue */}
          <div
            className="mt-6 rounded-2xl border border-teal/20 bg-teal/5 p-5"
            data-testid="before-you-continue"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck
                className="w-5 h-5 text-teal shrink-0 mt-0.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              <p className="text-ink leading-relaxed">
                <strong>Before you continue —</strong> trust your training.
                Trust the care plan. Trust your instincts. When in doubt,
                escalate.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* ── RED ────────────────────────────────────────────────────── */}
      <section
        data-testid="block-red"
        aria-labelledby="block-red-title"
        className="border-y border-red-200 bg-red-50"
      >
        <div className="container-jcc py-8 md:py-10">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-600 text-white"
              aria-hidden="true"
            >
              <AlertOctagon className="w-5 h-5" strokeWidth={2} />
            </span>
            <h2
              id="block-red-title"
              className="font-display text-2xl md:text-3xl font-bold text-red-900 tracking-tight"
            >
              🟥 RED — Stop, get help now
            </h2>
          </div>

          <p
            data-testid="block-red-subtitle"
            className="-mt-1 mb-3 text-sm md:text-base font-semibold uppercase tracking-wider text-red-700"
          >
            Emergency — Call 999
          </p>

          <p className="text-red-900 leading-relaxed text-base md:text-lg">
            <strong>Use this when:</strong> someone is in immediate danger,
            seriously unwell, has had a fall with possible injury, has a sudden
            change in consciousness or breathing, is being harmed, or{" "}
            <em>you</em> feel unsafe.
          </p>

          <ul className="mt-5 space-y-3" data-testid="block-red-list">
            <li className="flex gap-3 text-red-900 leading-relaxed">
              <span aria-hidden="true" className="text-red-700 font-bold">•</span>
              <span>
                Call{" "}
                <a
                  href="tel:999"
                  className="inline-flex items-center gap-1.5 rounded-full bg-red-700 text-white font-bold px-3 py-1 hover:bg-red-800 focus:outline-none focus:ring-4 focus:ring-red-200"
                  data-testid="call-999"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={2.25} />
                  999
                </a>{" "}
                where appropriate. Do not delay to seek permission.
              </span>
            </li>
            <li className="flex gap-3 text-red-900 leading-relaxed">
              <span aria-hidden="true" className="text-red-700 font-bold">•</span>
              <span>Stay with the person if it is safe to do so.</span>
            </li>
            <li className="flex gap-3 text-red-900 leading-relaxed">
              <span aria-hidden="true" className="text-red-700 font-bold">•</span>
              <span>
                Follow the agreed emergency plan and the person&apos;s care plan.
              </span>
            </li>
            <li className="flex gap-3 text-red-900 leading-relaxed">
              <span aria-hidden="true" className="text-red-700 font-bold">•</span>
              <span>
                Contact the named family or next of kin once safety is secured.
              </span>
            </li>
            <li className="flex gap-3 text-red-900 leading-relaxed">
              <span aria-hidden="true" className="text-red-700 font-bold">•</span>
              <span>
                Document what happened, when, what you did, and who you contacted.
              </span>
            </li>
          </ul>

          <p className="mt-5 text-sm text-red-900/85 italic leading-relaxed">
            Once immediate safety is secured, complete an Incident or Emergency
            / Escalation record.
          </p>
        </div>
      </section>

      {/* ── AMBER ──────────────────────────────────────────────────── */}
      <section
        data-testid="block-amber"
        aria-labelledby="block-amber-title"
        className="border-b border-amber-200 bg-amber-50"
      >
        <div className="container-jcc py-8 md:py-10">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-500 text-white"
              aria-hidden="true"
            >
              <AlertTriangle className="w-5 h-5" strokeWidth={2} />
            </span>
            <h2
              id="block-amber-title"
              className="font-display text-2xl md:text-3xl font-bold text-amber-900 tracking-tight"
            >
              🟡 AMBER — Pause and check
            </h2>
          </div>

          <p
            data-testid="block-amber-subtitle"
            className="-mt-1 mb-3 text-sm md:text-base font-semibold uppercase tracking-wider text-amber-700"
          >
            Urgent advice — Call 111 or GP Surgery
          </p>

          {/* Two action pills side by side where space allows */}
          <div
            className="mt-1 mb-5 flex flex-wrap gap-3"
            data-testid="amber-actions"
          >
            <a
              href="tel:111"
              data-testid="call-111"
              className="inline-flex items-center gap-2 rounded-full bg-amber-600 text-white font-bold px-4 py-2.5 text-sm md:text-base hover:bg-amber-700 focus:outline-none focus:ring-4 focus:ring-amber-200"
            >
              <PhoneCall className="w-4 h-4" strokeWidth={2.25} aria-hidden="true" />
              Call 111
            </a>

            {hasGp ? (
              <a
                href={`tel:${gpTel}`}
                data-testid="call-gp"
                className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-amber-600 text-amber-800 font-bold px-4 py-2.5 text-sm md:text-base hover:bg-amber-100 focus:outline-none focus:ring-4 focus:ring-amber-200"
              >
                <Phone className="w-4 h-4" strokeWidth={2.25} aria-hidden="true" />
                Call GP Surgery
                <span className="text-xs font-normal text-amber-800/80 hidden sm:inline">
                  ({gp.name ? `${gp.name} · ` : ""}{gp.phone})
                </span>
              </a>
            ) : (
              <button
                type="button"
                onClick={openEditGp}
                data-testid="add-gp"
                className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-dashed border-amber-600/70 text-amber-800 font-bold px-4 py-2.5 text-sm md:text-base hover:bg-amber-100 focus:outline-none focus:ring-4 focus:ring-amber-200"
                aria-label="Add GP Surgery phone number"
              >
                <PlusSquare className="w-4 h-4" strokeWidth={2.25} aria-hidden="true" />
                Add GP Surgery Number
              </button>
            )}

            {hasGp && (
              <button
                type="button"
                onClick={openEditGp}
                data-testid="edit-gp"
                className="inline-flex items-center gap-1.5 rounded-full text-amber-800 underline-offset-2 hover:underline px-2 py-2 text-sm"
                aria-label="Edit saved GP Surgery number"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
                Edit
              </button>
            )}
          </div>

          {hasGp && (
            <p className="-mt-3 mb-4 text-xs text-amber-900/80" data-testid="gp-saved-note">
              Saved on this phone only · Tap a number to open the dialler (it will not auto-call).
            </p>
          )}

          {!hasGp && !editingGp && (
            <p className="-mt-3 mb-4 text-xs text-amber-900/80" data-testid="gp-empty-note">
              Add the GP Surgery&apos;s number to enable one-tap dialling. Saved on this phone only — no account needed.
            </p>
          )}

          {editingGp && (
            <form
              onSubmit={saveGp}
              data-testid="gp-edit-form"
              className="mt-1 mb-5 rounded-2xl border-2 border-amber-300 bg-white p-4 md:p-5"
            >
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs font-semibold text-ink-muted uppercase tracking-wide mb-1">
                    Surgery name <span className="text-ink-soft font-normal">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={gpDraft.name}
                    onChange={(e) => setGpDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="e.g. Riverside Medical Practice"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none"
                    data-testid="gp-name-input"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold text-ink-muted uppercase tracking-wide mb-1">
                    Surgery phone number
                  </span>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    value={gpDraft.phone}
                    onChange={(e) => setGpDraft((d) => ({ ...d, phone: e.target.value }))}
                    placeholder="e.g. 01234 567890"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none"
                    data-testid="gp-phone-input"
                  />
                </label>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-600 text-white font-semibold px-4 py-2 text-sm hover:bg-amber-700 focus:outline-none focus:ring-4 focus:ring-amber-200"
                  data-testid="gp-save"
                >
                  Save number
                </button>
                <button
                  type="button"
                  onClick={cancelEditGp}
                  className="inline-flex items-center gap-2 rounded-full text-ink-muted hover:text-ink px-3 py-2 text-sm"
                  data-testid="gp-cancel"
                >
                  Cancel
                </button>
                {hasGp && (
                  <button
                    type="button"
                    onClick={clearGp}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full text-red-700 hover:bg-red-50 px-3 py-2 text-sm"
                    data-testid="gp-clear"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
                    Remove
                  </button>
                )}
              </div>
            </form>
          )}

          <p className="text-amber-900 leading-relaxed text-base md:text-lg">
            <strong>Use this when:</strong> something has changed, you have a
            concern, or you are unsure. For example: a change in how the person
            is presenting, an unfamiliar request, a transfer that hasn&apos;t
            been assessed as safe, missing information, unfamiliar equipment,
            or your gut telling you <em>&ldquo;this isn&apos;t right.&rdquo;</em>
          </p>

          <ul className="mt-5 space-y-3" data-testid="block-amber-list">
            {[
              "Stop the task or activity.",
              "Re-read the care plan and any relevant guidance.",
              "Seek appropriate advice — family, the named contact, GP, district nurse or out-of-hours services as relevant.",
              "Do not proceed with anything outside your training, competence, agreed role or the care plan.",
              "Document the concern and the action you took.",
            ].map((line) => (
              <li
                key={line}
                className="flex gap-3 text-amber-900 leading-relaxed"
              >
                <span aria-hidden="true" className="text-amber-700 font-bold">
                  •
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div
            className="mt-5 rounded-2xl border-2 border-amber-300 bg-amber-100/70 p-4"
            data-testid="amber-if-unsure"
          >
            <p className="text-amber-900 font-semibold leading-relaxed">
              If you are unsure, treat it as Amber and escalate.
            </p>
          </div>

          <p className="mt-4 text-sm text-amber-900/85 italic leading-relaxed">
            Use the Mood / Behaviour, Mobility, Safeguarding or Escalation forms
            as appropriate.
          </p>
        </div>
      </section>

      {/* ── GREEN ──────────────────────────────────────────────────── */}
      <section
        data-testid="block-green"
        aria-labelledby="block-green-title"
        className="border-b border-emerald-200 bg-emerald-50"
      >
        <div className="container-jcc py-8 md:py-10">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-600 text-white"
              aria-hidden="true"
            >
              <CheckCircle2 className="w-5 h-5" strokeWidth={2} />
            </span>
            <h2
              id="block-green-title"
              className="font-display text-2xl md:text-3xl font-bold text-emerald-900 tracking-tight"
            >
              🟢 GREEN — Continue as planned
            </h2>
          </div>

          <p className="text-emerald-900 leading-relaxed text-base md:text-lg">
            <strong>Use this when:</strong> the situation matches the agreed
            care plan, equipment and routine, and you are working within your
            training and competence.
          </p>

          <ul className="mt-5 space-y-3" data-testid="block-green-list">
            {[
              "Follow the agreed plan.",
              "Keep observing — small changes can matter.",
              "Record what you provided, what you noticed and any relevant information at the end of the shift.",
            ].map((line) => (
              <li
                key={line}
                className="flex gap-3 text-emerald-900 leading-relaxed"
              >
                <span aria-hidden="true" className="text-emerald-700 font-bold">
                  •
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Add to Home Screen ─────────────────────────────────────── */}
      <section
        data-testid="add-to-home"
        className="bg-white border-b border-slate-200"
      >
        <div className="container-jcc py-9 md:py-12">
          <div className="flex items-center gap-3 mb-3">
            <Bookmark
              className="w-5 h-5 text-teal"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight">
              Keep this page one tap away
            </h2>
          </div>
          <p className="text-ink-muted leading-relaxed max-w-2xl">
            Save Stop &amp; Check to your phone home screen so it&apos;s ready
            when you need it.
          </p>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {/* iPhone */}
            <article
              className="rounded-2xl border border-slate-200 p-5 bg-[#fafaf7]"
              data-testid="ios-instructions"
            >
              <div className="flex items-center gap-2 mb-3">
                <Smartphone
                  className="w-5 h-5 text-ink"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <h3 className="font-display text-base font-semibold">iPhone (Safari)</h3>
              </div>
              <ol className="space-y-2 text-sm text-ink leading-relaxed list-decimal pl-5">
                <li>
                  Tap the <strong>Share</strong> icon{" "}
                  <Share
                    className="inline w-4 h-4 align-text-bottom"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />{" "}
                  at the bottom of the screen.
                </li>
                <li>
                  Scroll down and tap <strong>Add to Home Screen</strong>{" "}
                  <PlusSquare
                    className="inline w-4 h-4 align-text-bottom"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  .
                </li>
                <li>
                  Confirm the name (e.g. &ldquo;Stop &amp; Check&rdquo;) and tap{" "}
                  <strong>Add</strong>.
                </li>
              </ol>
            </article>

            {/* Android */}
            <article
              className="rounded-2xl border border-slate-200 p-5 bg-[#fafaf7]"
              data-testid="android-instructions"
            >
              <div className="flex items-center gap-2 mb-3">
                <Smartphone
                  className="w-5 h-5 text-ink"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <h3 className="font-display text-base font-semibold">
                  Android (Chrome)
                </h3>
              </div>
              <ol className="space-y-2 text-sm text-ink leading-relaxed list-decimal pl-5">
                <li>
                  Tap the <strong>three-dot menu</strong>{" "}
                  <MoreVertical
                    className="inline w-4 h-4 align-text-bottom"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />{" "}
                  in the top-right.
                </li>
                <li>
                  Tap <strong>Add to Home screen</strong> (or{" "}
                  <strong>Install app</strong>).
                </li>
                <li>
                  Confirm the name (e.g. &ldquo;Stop &amp; Check&rdquo;) and tap{" "}
                  <strong>Add</strong>.
                </li>
              </ol>
            </article>
          </div>
        </div>
      </section>

      {/* ── Disclaimer ─────────────────────────────────────────────── */}
      <section
        data-testid="safety-disclaimer"
        className="bg-[#f7f7f3] border-b border-slate-200"
      >
        <div className="container-jcc py-9 md:py-12">
          <h2 className="font-display text-base font-semibold text-ink mb-3">
            A plain-English note about this page
          </h2>
          <p className="text-sm md:text-base text-ink-muted leading-relaxed">
            This page is a brief, calm thinking prompt for self-employed carers.{" "}
            <strong className="text-ink">
              It is not clinical advice, a diagnostic tool, or a substitute for
              emergency services, the person&apos;s care plan, the agreed care
              arrangements and named contacts, your employer&apos;s or
              contracting party&apos;s procedures, your training, or your own
              professional judgement.
            </strong>{" "}
            In an emergency, call <strong className="text-ink">999</strong>{" "}
            without delay. Just Care Community is not a care agency and does
            not direct, supervise or monitor individual care arrangements. You
            remain responsible for the decisions you make on shift.
          </p>
        </div>
      </section>
    </div>
  );
}
